import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import type { ParsedResult } from "./BulkUpload";

interface Props {
  parsed: ParsedResult;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}

export function ValidationPreview({ parsed, onConfirm, onCancel, saving }: Props) {
  const validRows = parsed.rows.filter((r) => r.errors.length === 0);
  const errorRows = parsed.rows.filter((r) => r.errors.length > 0);
  const hasErrors = errorRows.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {hasErrors ? (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          )}
          Validation Preview
        </CardTitle>
        <CardDescription>
          {validRows.length} valid rows &middot; {errorRows.length} rows with errors &middot; {parsed.moduleCodes.length} modules
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasErrors && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
            <p className="font-medium text-amber-800 mb-2">Errors Found ({parsed.totalErrors})</p>
            <div className="space-y-1 max-h-32 overflow-auto text-xs text-amber-700">
              {errorRows.map((r) => (
                <div key={r.sn}>
                  <span className="font-medium">Row {r.sn} ({r.studentId || "??"}):</span>{" "}
                  {r.errors.join("; ")}
                </div>
              ))}
            </div>
          </div>
        )}

        <ScrollArea className="max-h-[60vh] w-full">
          <div className="min-w-max">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">SN</TableHead>
                  <TableHead className="whitespace-nowrap">Form IV Index</TableHead>
                  <TableHead className="whitespace-nowrap">Student ID</TableHead>
                  <TableHead className="whitespace-nowrap">Name</TableHead>
                  {parsed.moduleCodes.map((c) => (
                    <TableHead key={c} className="text-center w-16 whitespace-nowrap">{c}</TableHead>
                  ))}
                  <TableHead className="w-12">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsed.rows.map((r) => (
                  <TableRow key={r.sn} className={r.errors.length > 0 ? "bg-red-50/50" : ""}>
                    <TableCell className="text-xs">{r.sn}</TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">{r.formIvIndex || "—"}</TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">{r.studentId !== r.formIvIndex ? r.studentId : "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{r.studentName || "—"}</TableCell>
                    {parsed.moduleCodes.map((c) => (
                      <TableCell key={c} className="text-center text-xs">{r.scores[c] ?? "—"}</TableCell>
                    ))}
                    <TableCell>
                      {r.errors.length > 0 ? (
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                          {r.errors.length} err
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200 text-[10px]">OK</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {parsed.rows.length > 200 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              Showing all {parsed.rows.length} rows (scroll to view)
            </p>
          )}
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <div className="flex items-center gap-3 pt-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={saving || validRows.length === 0}
            className="ml-auto"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {saving ? "Saving..." : `Import ${validRows.length} Valid Results`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
